/** RFC 0345 S6: shared metadata is not authority. Sign canonical records,
 * including their domain and target, and check live person-device membership.
 */
import { Automerge } from '@automerge/automerge-repo/slim'
import { Access, GroupId } from '@automerge/automerge-repo-keyhive'
import { hex, unhex } from './invite'
import type { createSharingRepo } from './repo'

export type Sharing = Awaited<ReturnType<typeof createSharingRepo>>
export const KEY_ID = /^[0-9a-f]{64}$/
const SIGNATURE = /^[0-9a-f]{128}$/

export function scalar(value: unknown): string {
  return typeof value === 'string' || Automerge.isImmutableString(value)
    ? String(value)
    : ''
}

function stable(value: unknown): unknown {
  if (Automerge.isImmutableString(value)) return String(value)
  if (Array.isArray(value)) return value.map(stable)
  if (value && typeof value === 'object')
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([key, item]) => [key, stable(item)]),
    )
  return value
}

export function canonicalBytes(value: unknown): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(stable(value)))
}

export async function signBytes(
  sharing: Sharing,
  bytes: Uint8Array,
): Promise<string> {
  return hex(
    new Uint8Array(
      await crypto.subtle.sign(
        'Ed25519',
        sharing.hive.active.keyPair.privateKey,
        bytes as BufferSource,
      ),
    ),
  )
}

export async function verifyBytes(
  signer: string,
  signature: string,
  bytes: Uint8Array,
): Promise<boolean> {
  if (!KEY_ID.test(signer) || !SIGNATURE.test(signature)) return false
  const publicKey = await crypto.subtle.importKey(
    'raw',
    unhex(signer) as BufferSource,
    'Ed25519',
    false,
    ['verify'],
  )
  return crypto.subtle.verify(
    'Ed25519',
    publicKey,
    unhex(signature) as BufferSource,
    bytes as BufferSource,
  )
}

export async function isPersonDevice(
  sharing: Sharing,
  person: string,
  signer: string,
): Promise<boolean> {
  if (!KEY_ID.test(person) || !KEY_ID.test(signer)) return false
  const group = await sharing.hive.keyhive.getGroup(new GroupId(unhex(person)))
  return ((await group?.transitiveMembers()) ?? []).some(
    (member) =>
      hex(member.who.id.toBytes()) === signer &&
      member.can.equals(Access.admin()),
  )
}
